import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonsContainer, ButtonsContainerAlignment } from 'record-it-components/button/ButtonsContainer/ButtonsContainer';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { LocationInput, LocationInputProps } from 'record-it-components/input/LocationInput/LocationInput';
import { BufferedTextInput, TextInputProps } from 'record-it-components/input/TextInput/TextInput';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { TimeTrackingActivityCompilationSelect, TimeTrackingActivityCompilationSelectProps } from '../../timeTrackingActivityCompilation/TimeTrackingActivityCompilationSelect/TimeTrackingActivityCompilationSelect';
export type CreateConstructionSiteFormProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  userGroupId: string;
  defaultTimeTrackingActivityCompilationId: string;
  onCreateConstructionSite: (constructionSiteToCreate: ConstructionSiteToCreate) => void;
  onCancel: () => void;
};
export const CreateConstructionSiteForm: FunctionComponent<CreateConstructionSiteFormProps> = React.memo(props => {
  const $ = _c(57);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let defaultTimeTrackingActivityCompilationId;
  let onCancel;
  let onCreateConstructionSite;
  let remainingProps;
  let userGroupId;
  if ($[2] !== t0) {
    ({
      userGroupId,
      defaultTimeTrackingActivityCompilationId,
      onCreateConstructionSite,
      onCancel,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = defaultTimeTrackingActivityCompilationId;
    $[4] = onCancel;
    $[5] = onCreateConstructionSite;
    $[6] = remainingProps;
    $[7] = userGroupId;
  } else {
    defaultTimeTrackingActivityCompilationId = $[3];
    onCancel = $[4];
    onCreateConstructionSite = $[5];
    remainingProps = $[6];
    userGroupId = $[7];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[8] !== defaultTimeTrackingActivityCompilationId) {
    t1 = () => ({
      name: "",
      location: null,
      compilationId: defaultTimeTrackingActivityCompilationId
    });
    $[8] = defaultTimeTrackingActivityCompilationId;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const [constructionSiteToCreate, setConstructionSiteToCreate] = useState(t1);
  let t2;
  if ($[10] !== constructionSiteToCreate) {
    t2 = name => {
      setConstructionSiteToCreate({
        ...constructionSiteToCreate,
        name
      });
    };
    $[10] = constructionSiteToCreate;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleNameChanged = useEventCallback(t2);
  let t3;
  if ($[12] !== constructionSiteToCreate) {
    t3 = compilationId => {
      setConstructionSiteToCreate({
        ...constructionSiteToCreate,
        compilationId
      });
    };
    $[12] = constructionSiteToCreate;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleCompilationIdChanged = useEventCallback(t3);
  let t4;
  if ($[14] !== constructionSiteToCreate) {
    t4 = location => {
      setConstructionSiteToCreate({
        ...constructionSiteToCreate,
        location
      });
    };
    $[14] = constructionSiteToCreate;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleLocationChanged = useEventCallback(t4);
  let t5;
  if ($[16] !== constructionSiteToCreate || $[17] !== onCreateConstructionSite) {
    t5 = () => {
      onCreateConstructionSite(constructionSiteToCreate);
    };
    $[16] = constructionSiteToCreate;
    $[17] = onCreateConstructionSite;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleCreateClick = useEventCallback(t5);
  let t6;
  if ($[19] !== t) {
    t6 = t("entities.ConstructionSite.name");
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== constructionSiteToCreate.name || $[22] !== handleNameChanged || $[23] !== t6) {
    t7 = <FormGroup><BufferedTextInput label={t6} value={constructionSiteToCreate.name} onInput={handleNameChanged} data-testid="CreateConstructionSiteForm.NameInput" /></FormGroup>;
    $[21] = constructionSiteToCreate.name;
    $[22] = handleNameChanged;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const t8 = constructionSiteToCreate.compilationId;
  let t9;
  if ($[25] !== t) {
    t9 = t("entities.ConstructionSite.compilationId");
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== constructionSiteToCreate.compilationId || $[28] !== handleCompilationIdChanged || $[29] !== t9 || $[30] !== userGroupId) {
    t10 = <FormGroup><TimeTrackingActivityCompilationSelect value={t8} label={t9} userGroupId={userGroupId} onValueChanged={handleCompilationIdChanged} /></FormGroup>;
    $[27] = constructionSiteToCreate.compilationId;
    $[28] = handleCompilationIdChanged;
    $[29] = t9;
    $[30] = userGroupId;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const t11 = constructionSiteToCreate.location;
  let t12;
  if ($[32] !== t) {
    t12 = t("entities.ConstructionSite.location");
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== constructionSiteToCreate.location || $[35] !== handleLocationChanged || $[36] !== t12) {
    t13 = <FormGroup><LocationInput value={t11} label={t12} onValueChanged={handleLocationChanged} /></FormGroup>;
    $[34] = constructionSiteToCreate.location;
    $[35] = handleLocationChanged;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t) {
    t14 = t("components.constructionSite.CreateConstructionSiteForm.cancel");
    $[38] = t;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== onCancel || $[41] !== t14) {
    t15 = <ButtonStandard type={ButtonStandardType.Secondary} onClick={onCancel} data-testid="CreateConstructionSiteForm.CancelButton">{t14}</ButtonStandard>;
    $[40] = onCancel;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t) {
    t16 = t("components.constructionSite.CreateConstructionSiteForm.create");
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== handleCreateClick || $[46] !== t16) {
    t17 = <ButtonStandard type={ButtonStandardType.Primary} onClick={handleCreateClick} data-testid="CreateConstructionSiteForm.CreateButton">{t16}</ButtonStandard>;
    $[45] = handleCreateClick;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t15 || $[49] !== t17) {
    t18 = <ButtonsContainer alignment={ButtonsContainerAlignment.Right}>{t15}{t17}</ButtonsContainer>;
    $[48] = t15;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== remainingProps || $[52] !== t10 || $[53] !== t13 || $[54] !== t18 || $[55] !== t7) {
    t19 = <div {...remainingProps}>{t7}{t10}{t13}{t18}</div>;
    $[51] = remainingProps;
    $[52] = t10;
    $[53] = t13;
    $[54] = t18;
    $[55] = t7;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
});
export type ConstructionSiteToCreate = Pick<ConstructionSite, 'name' | 'location' | 'compilationId'>;